/**
 * Sel-2 video slider — powered by Glider.js
 * https://nickpiscitelli.github.io/Glider.js/
 * Keeps Sel-2 design; Glider handles swipe / drag / dots / responsive slides.
 */
(function () {
  'use strict';

  function qs(root, sel) {
    return root.querySelector(sel);
  }

  function qsa(root, sel) {
    return Array.prototype.slice.call(root.querySelectorAll(sel));
  }

  function toNumber(value, fallback) {
    var n = parseFloat(value);
    return isNaN(n) ? fallback : n;
  }

  function normalizeId(value) {
    var id = String(value || '').trim();
    if (!id) return '';
    if (id.charAt(0) === '#') id = id.slice(1);
    return id;
  }

  function mobileSlidesToShow(root) {
    var slides = toNumber(root.getAttribute('data-slides-mobile'), 1);
    var teaser = toNumber(root.getAttribute('data-teaser-mobile'), 0.35);
    return Math.max(1, slides + teaser);
  }

  function Sel2VideoSlider(root) {
    this.root = root;
    this.gliderEl = qs(root, '[data-sel-2-video-glider]');
    this.dotsEl = qs(root, '[data-sel-2-video-dots]');
    this.prevBtn = qs(root, '[data-sel-2-video-prev]');
    this.nextBtn = qs(root, '[data-sel-2-video-next]');
    this.slides = qsa(root, '[data-sel-2-video-slide]');
    this.glider = null;
    this.showArrows = root.getAttribute('data-show-arrows') === 'true';

    if (!this.gliderEl || !this.slides.length || typeof window.Glider !== 'function') return;

    this.initGlider();
    this.bindNav();
    this.bindPlay();
    this.bindCta();
    this.updateNavVisibility();
  }

  Sel2VideoSlider.prototype.initGlider = function () {
    var root = this.root;
    var slidesDesktop = toNumber(root.getAttribute('data-slides-desktop'), 4);
    var showDots = root.getAttribute('data-show-dots') === 'true' && this.dotsEl;
    var self = this;

    this.glider = new window.Glider(this.gliderEl, {
      slidesToShow: mobileSlidesToShow(root),
      slidesToScroll: 1,
      draggable: true,
      dragVelocity: 2.8,
      scrollLock: true,
      scrollLockDelay: 50,
      resizeLock: true,
      duration: 0.18,
      easing: function (_x, t, b, c, d) {
        // Linear ease — snappier than default ease-in
        return c * (t / d) + b;
      },
      dots: showDots ? this.dotsEl : undefined,
      responsive: [
        {
          breakpoint: 750,
          settings: {
            slidesToShow: slidesDesktop,
            slidesToScroll: 1,
            duration: 0.18
          }
        }
      ]
    });

    // Keep Sel-2 dot styling class in sync with Glider dots
    if (showDots) {
      this.dotsEl.classList.add('sel-2-video-slider__dots');
      this.styleDots();
      this.gliderEl.addEventListener('glider-refresh', function () {
        self.styleDots();
        self.updateNavVisibility();
      });
      this.gliderEl.addEventListener('glider-animated', function () {
        self.styleDots();
        self.updateNavVisibility();
      });
    } else {
      this.gliderEl.addEventListener('glider-refresh', function () {
        self.updateNavVisibility();
      });
      this.gliderEl.addEventListener('glider-animated', function () {
        self.updateNavVisibility();
      });
    }

    this.gliderEl.addEventListener('scroll', function () {
      self.updateNavVisibility();
    });
  };

  Sel2VideoSlider.prototype.bindNav = function () {
    var self = this;
    if (!this.showArrows) return;

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', function (e) {
        e.preventDefault();
        if (self.glider && typeof self.glider.scrollItem === 'function') {
          self.glider.scrollItem('prev');
        }
      });
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', function (e) {
        e.preventDefault();
        if (self.glider && typeof self.glider.scrollItem === 'function') {
          self.glider.scrollItem('next');
        }
      });
    }
  };

  Sel2VideoSlider.prototype.updateNavVisibility = function () {
    if (!this.showArrows) {
      if (this.prevBtn) this.prevBtn.hidden = true;
      if (this.nextBtn) this.nextBtn.hidden = true;
      return;
    }
    if (!this.gliderEl) return;

    var maxScroll = Math.max(0, this.gliderEl.scrollWidth - this.gliderEl.clientWidth);
    var left = this.gliderEl.scrollLeft || 0;
    var atStart = left <= 2;
    var atEnd = maxScroll <= 2 || left >= maxScroll - 2;

    if (this.prevBtn) this.prevBtn.hidden = atStart;
    if (this.nextBtn) this.nextBtn.hidden = atEnd;
  };

  Sel2VideoSlider.prototype.styleDots = function () {
    if (!this.dotsEl) return;
    qsa(this.dotsEl, '.glider-dot').forEach(function (dot) {
      dot.classList.add('sel-2-video-slider__dot');
    });
  };

  Sel2VideoSlider.prototype.stopOtherVideos = function (exceptSlide) {
    var self = this;
    this.slides.forEach(function (slide) {
      if (slide === exceptSlide) return;
      self.restorePlayUi(slide);
    });
  };

  Sel2VideoSlider.prototype.restorePlayUi = function (slide) {
    var player = qs(slide, '[data-sel-2-video-player]');
    var playBtn = qs(slide, '[data-sel-2-video-play]');
    var overlay = qs(slide, '.sel-2-video-slide__overlay');
    var poster = qs(slide, '.sel-2-video-slide__poster');
    if (player) {
      player.innerHTML = '';
      player.hidden = true;
    }
    slide.classList.remove('is-playing');
    if (playBtn) playBtn.hidden = false;
    if (overlay) overlay.hidden = false;
    if (poster) poster.hidden = false;
  };

  Sel2VideoSlider.prototype.playSlide = function (slide, btn) {
    var player = qs(slide, '[data-sel-2-video-player]');
    if (!player || !btn) return;

    this.stopOtherVideos(slide);

    var type = btn.getAttribute('data-video-type');
    player.innerHTML = '';
    player.hidden = false;
    slide.classList.add('is-playing');
    btn.hidden = true;

    var overlay = qs(slide, '.sel-2-video-slide__overlay');
    var poster = qs(slide, '.sel-2-video-slide__poster');
    if (overlay) overlay.hidden = true;
    if (poster) poster.hidden = true;

    var self = this;

    if (type === 'shopify') {
      var tpl = qs(slide, '[data-sel-2-video-template]');
      if (!tpl) return;
      player.appendChild(tpl.content.cloneNode(true));
      var vid = qs(player, 'video');
      if (!vid) return;
      vid.addEventListener('ended', function () {
        self.restorePlayUi(slide);
      });
      var p = vid.play && vid.play();
      if (p && p.catch) p.catch(function () {});
      return;
    }

    if (type === 'embed') {
      var url = btn.getAttribute('data-embed-url');
      if (!url) return;
      var iframe = document.createElement('iframe');
      iframe.src = url;
      iframe.title = 'Customer story video';
      iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
      iframe.allowFullscreen = true;
      iframe.setAttribute('playsinline', '');
      iframe.className = 'sel-2-video-slide__iframe';
      player.appendChild(iframe);
      return;
    }

    if (type === 'file') {
      var src = btn.getAttribute('data-video-src');
      if (!src) return;
      var fileVid = document.createElement('video');
      fileVid.src = src;
      fileVid.controls = true;
      fileVid.playsInline = true;
      fileVid.autoplay = true;
      fileVid.className = 'sel-2-video-slide__video';
      fileVid.addEventListener('ended', function () {
        self.restorePlayUi(slide);
      });
      player.appendChild(fileVid);
      var playPromise = fileVid.play();
      if (playPromise && playPromise.catch) playPromise.catch(function () {});
    }
  };

  Sel2VideoSlider.prototype.bindPlay = function () {
    var self = this;
    this.root.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-sel-2-video-play]');
      if (!btn || !self.root.contains(btn) || btn.disabled) return;
      // Ignore clicks that Glider marked as drag-ends
      if (self.gliderEl && self.gliderEl.classList.contains('drag')) return;
      e.preventDefault();
      var slide = btn.closest('[data-sel-2-video-slide]');
      if (slide) self.playSlide(slide, btn);
    });
  };

  Sel2VideoSlider.prototype.bindCta = function () {
    var cta = qs(this.root, '[data-sel-2-video-cta]');
    if (!cta) return;

    cta.addEventListener('click', function (e) {
      var targetId = normalizeId(cta.getAttribute('data-scroll-target'));
      if (!targetId) return;
      var target = document.getElementById(targetId);
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  function initAll(scope) {
    qsa(scope || document, '[data-sel-2-video-slider]').forEach(function (root) {
      if (root.getAttribute('data-sel-2-video-slider-ready') === 'true') return;
      if (!qs(root, '[data-sel-2-video-glider]')) return;
      if (typeof window.Glider !== 'function') return;
      root.setAttribute('data-sel-2-video-slider-ready', 'true');
      new Sel2VideoSlider(root);
    });
  }

  function boot() {
    initAll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Glider is deferred — retry briefly if needed
  window.addEventListener('load', function () {
    initAll();
  });

  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target || document;
    qsa(section, '[data-sel-2-video-slider]').forEach(function (root) {
      root.removeAttribute('data-sel-2-video-slider-ready');
    });
    initAll(section);
  });
})();
